import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Portal from '../atoms/Portal';
import { Search, Check, Plus } from 'lucide-react';

function filterOptions(options, query) {
  const q = String(query).toLowerCase();
  return options.filter((opt) => {
    const lbl = typeof opt === 'object' ? opt.label : opt;
    return String(lbl).toLowerCase().includes(q);
  });
}

export default function ComboBox({
  label,
  options = [],
  value,
  onChange,
  placeholder = 'Type to search...',
  className = '',
  hideIcon = false,
  allowCreate = false,
  createLabel = 'ke entry',
  onCreate,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 220 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const inputRef = useRef(null);
  const valueRef = useRef(value);
  const optionsRef = useRef(options);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Sync the display label when the controlled value/options change without
  // calling setState from inside an effect (react-hooks/set-state-in-effect).
  const [prevSync, setPrevSync] = useState({ value, options });
  if (prevSync.value !== value || prevSync.options !== options) {
    setPrevSync({ value, options });
    const selected = options.find((opt) => (typeof opt === 'object' ? opt.value === value : opt === value));
    const labelText = selected ? (typeof selected === 'object' ? selected.label : selected) : (value || '');
    setQuery(labelText);
    setActiveIndex(-1);
  }

  const filteredOptions = useMemo(() => filterOptions(options, query), [options, query]);
  const showCreate = allowCreate && Boolean(query.trim()) && filteredOptions.length === 0;

  const openDropdown = () => {
    if (disabled) return;
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    setIsOpen(true);
  };

  const handleInputFocus = (e) => {
    if (disabled) return;
    e.target.select();
    openDropdown();
  };

  const handleSelectOption = (optVal, optLbl) => {
    if (onChange) {
      onChange(optVal);
    }
    setQuery(optLbl);
    setActiveIndex(-1);
    setIsOpen(false);
  };

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
    const selected = optionsRef.current.find((opt) =>
      typeof opt === 'object' ? opt.value === valueRef.current : opt === valueRef.current
    );
    setQuery(
      selected
        ? typeof selected === 'object'
          ? selected.label
          : selected
        : valueRef.current || ''
    );
  }, []);

  const handleCreate = () => {
    const text = query.trim();
    if (!text) return;
    if (onCreate) onCreate(text);
    else if (onChange) onChange(text);
    setActiveIndex(-1);
    setIsOpen(false);
  };

  const selectActive = () => {
    if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
      const opt = filteredOptions[activeIndex];
      const optVal = typeof opt === 'object' ? opt.value : opt;
      const optLbl = typeof opt === 'object' ? opt.label : opt;
      handleSelectOption(optVal, optLbl);
      return true;
    }
    if (showCreate && activeIndex === filteredOptions.length) {
      handleCreate();
      return true;
    }
    return false;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      closeDropdown();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        openDropdown();
        const total = filteredOptions.length + (showCreate ? 1 : 0);
        setActiveIndex(total > 0 ? 0 : -1);
        return;
      }
      const total = filteredOptions.length + (showCreate ? 1 : 0);
      setActiveIndex((i) => (i < 0 ? 0 : Math.min(i + 1, total - 1)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? -1 : i - 1));
      return;
    }
    if (e.key === 'Tab' || e.key === 'Enter') {
      if (isOpen && selectActive()) e.preventDefault();
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      const isOutsideTrigger = triggerRef.current && !triggerRef.current.contains(e.target);
      const isOutsidePopover = popoverRef.current && !popoverRef.current.contains(e.target);

      if (isOutsideTrigger && isOutsidePopover) {
        closeDropdown();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeDropdown]);

  return (
    <div className={`form-group ${className}${disabled ? ' is-disabled' : ''}`} ref={triggerRef}>
      {label && <label>{label}</label>}
      <div className={hideIcon ? 'input-wrapper' : 'input-wrapper input-wrapper--has-prefix'}>
        {!hideIcon && (
          <span className="input-icon-prefix">
            <Search size={16} />
          </span>
        )}
        <input
          ref={inputRef}
          type="text"
          className="input-field"
          placeholder={placeholder}
          value={query}
          disabled={disabled}
          onFocus={handleInputFocus}
          onClick={handleInputFocus}
          onChange={(e) => {
            if (disabled) return;
            const text = e.target.value;
            setQuery(text);
            setIsOpen(true);
            const matches = filterOptions(options, text);
            setActiveIndex(
              matches.length > 0 ? 0 : allowCreate && text.trim() ? matches.length : -1,
            );
          }}
          onKeyDown={disabled ? undefined : handleKeyDown}
          onBlur={() => {
            setTimeout(() => {
              if (popoverRef.current && popoverRef.current.contains(document.activeElement)) return;
              closeDropdown();
            }, 100);
          }}
        />
      </div>

      {isOpen && (
        <Portal>
          <div
            ref={popoverRef}
            className="portal-popover custom-dropdown-menu"
            style={{
              top: coords.top,
              left: coords.left,
              width: coords.width,
            }}
          >
            {filteredOptions.map((opt, idx) => {
              const optVal = typeof opt === 'object' ? opt.value : opt;
              const optLbl = typeof opt === 'object' ? opt.label : opt;
              const isSelected = value === optVal;
              const isActive = idx === activeIndex;
              return (
                <div
                  key={idx}
                  className={`dropdown-item ${isSelected ? 'dropdown-item--selected' : ''}${
                    isActive ? ' dropdown-item--highlighted' : ''
                  }`}
                  onClick={() => handleSelectOption(optVal, optLbl)}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActiveIndex(idx)}
                >
                  <span>{optLbl}</span>
                  {isSelected && <Check size={14} />}
                </div>
              );
            })}

            {filteredOptions.length === 0 && !allowCreate && (
              <div className="dropdown-item dropdown-item--no-results">
                Tidak ada data
              </div>
            )}

            {showCreate && (
              <div
                className={`dropdown-item dropdown-item--create${
                  activeIndex === filteredOptions.length ? ' dropdown-item--highlighted' : ''
                }`}
                onClick={handleCreate}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(filteredOptions.length)}
              >
                <Plus size={14} />
                <span>Tambah '{query}' {createLabel}</span>
              </div>
            )}
          </div>
        </Portal>
      )}
    </div>
  );
}
